import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Container from '@mui/material/Container';
import CssBaseline from '@mui/material/CssBaseline';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { Link } from 'react-router-dom';

import SecurityRoundedIcon from '@mui/icons-material/SecurityRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import HourglassEmptyRoundedIcon from '@mui/icons-material/HourglassEmptyRounded';
import AssignmentRoundedIcon from '@mui/icons-material/AssignmentRounded';
import BiotechRoundedIcon from '@mui/icons-material/BiotechRounded';
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded';
import ScienceRoundedIcon from '@mui/icons-material/ScienceRounded';
import ApiRoundedIcon from '@mui/icons-material/ApiRounded';
import VerifiedUserRoundedIcon from '@mui/icons-material/VerifiedUserRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import BallotRoundedIcon from '@mui/icons-material/BallotRounded';

import AppAppBar from '../components/AppAppBar';
import Pricing from '../components/Pricing';
import FAQ, { LS_QUESTIONS } from '../components/FAQ';
import Footer from '../components/Footer';
import getLPTheme from './getLPTheme';

// ─── Section: Hero ───────────────────────────────────────────────────────────
function Hero() {
  return (
    <Box
      id="ls-hero"
      sx={{
        width: '100%',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #060e1e 0%, #0a1628 50%, #07111f 100%)',
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Subtle grid pattern overlay */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage:
            'repeating-linear-gradient(0deg, transparent, transparent 39px, rgba(56,189,248,0.04) 39px, rgba(56,189,248,0.04) 40px), repeating-linear-gradient(90deg, transparent, transparent 39px, rgba(56,189,248,0.04) 39px, rgba(56,189,248,0.04) 40px)',
          pointerEvents: 'none',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: '20%',
          right: '-10%',
          width: 600,
          height: 600,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(52,211,153,0.06) 0%, transparent 70%)',
          pointerEvents: 'none',
        }}
      />

      <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1, py: { xs: 16, sm: 14 }, textAlign: 'center' }}>
        <Stack spacing={3} alignItems="center">
          <Chip
            label="CDISC SDTM · MedDRA Coded · Phase I–IV"
            size="small"
            sx={{
              background: 'rgba(52,211,153,0.12)',
              border: '1px solid rgba(52,211,153,0.35)',
              color: '#34d399',
              fontWeight: 600,
              fontSize: '0.75rem',
              letterSpacing: '0.03em',
            }}
          />
          <Typography
            variant="h1"
            sx={{
              fontSize: 'clamp(2.2rem, 5vw, 3.4rem)',
              fontWeight: 800,
              color: '#f0f9ff',
              lineHeight: 1.12,
              textShadow: '0 2px 24px rgba(6,14,30,0.8)',
            }}
          >
            Stop hand-crafting clinical test data.{' '}
            <Box component="span" sx={{ color: '#34d399' }}>
              Ship with confidence.
            </Box>
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: '#bae6fd',
              fontSize: '1.1rem',
              lineHeight: 1.75,
              maxWidth: 620,
              textShadow: '0 1px 12px rgba(6,14,30,0.9)',
            }}
          >
            Cynthia generates CDISC SDTM-compliant synthetic trial data on demand — so your
            EDC, CTMS, and data pipelines are fully tested before go-live, without touching
            a single real patient record.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ pt: 1 }}>
            <Button
              component={Link}
              to="/signup"
              variant="contained"
              size="large"
              sx={{ fontWeight: 700, px: 4 }}
            >
              Start Free — No Credit Card
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => document.getElementById('ls-how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
              sx={{
                borderColor: 'rgba(56,189,248,0.35)',
                color: '#38bdf8',
                '&:hover': { borderColor: '#38bdf8', bgcolor: 'rgba(56,189,248,0.06)' },
              }}
            >
              See How It Works
            </Button>
          </Stack>
          <Stack direction="row" flexWrap="wrap" justifyContent="center" gap={1} sx={{ pt: 0.5 }}>
            {[
              'SDTM DM · AE · LB · VS · EX · DS',
              'Phase I–IV protocols',
              '7 therapeutic areas',
            ].map((item, i, arr) => (
              <React.Fragment key={item}>
                <Typography variant="caption" sx={{ color: '#475569', fontSize: '0.78rem' }}>{item}</Typography>
                {i < arr.length - 1 && (
                  <Typography variant="caption" sx={{ color: '#334155', fontSize: '0.78rem' }}>·</Typography>
                )}
              </React.Fragment>
            ))}
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
}

// ─── Section: Pain Points ────────────────────────────────────────────────────
const PAIN_POINTS = [
  {
    icon: <SecurityRoundedIcon sx={{ fontSize: 32 }} />,
    title: 'Real patient data in dev/test = compliance risk',
    body: 'Every IRB waiver, BAA negotiation, and de-identification audit is time your team could spend shipping. One breach wipes out the clinical program.',
  },
  {
    icon: <WarningAmberRoundedIcon sx={{ fontSize: 32 }} />,
    title: 'Hand-crafted fixtures break under protocol changes',
    body: 'When the protocol amendment drops — new arm, new visit, new AE code — your test data is instantly stale. QA halts while someone builds it back by hand.',
  },
  {
    icon: <HourglassEmptyRoundedIcon sx={{ fontSize: 32 }} />,
    title: 'QA delays cost trials weeks of runway',
    body: 'Waiting on realistic test datasets to validate your EDC or analytics pipeline is a silent tax on every sprint. Those weeks compound across studies.',
  },
];

function PainPoints() {
  return (
    <Box
      sx={{
        pt: { xs: 4, sm: 12 },
        pb: { xs: 8, sm: 12 },
        color: 'white',
        bgcolor: '#06090a',
        borderTop: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Container>
        <Box sx={{ width: { sm: '100%', md: '60%' }, textAlign: { sm: 'left', md: 'center' }, mx: 'auto', mb: 6 }}>
          <Typography component="h2" variant="h4" color="text.primary">
            The reality for CROs and life sciences data teams
          </Typography>
          <Typography variant="body1" sx={{ color: 'grey.400', mt: 1 }}>
            Test data that mirrors your production EDC shouldn't take weeks to fabricate.
          </Typography>
        </Box>
        <Grid container spacing={2.5}>
          {PAIN_POINTS.map((p) => (
            <Grid item xs={12} md={4} key={p.title}>
              <Stack
                direction="column"
                color="inherit"
                component={Card}
                spacing={1}
                useFlexGap
                sx={{
                  p: 3,
                  height: '100%',
                  border: '1px solid',
                  borderColor: 'grey.800',
                  background: 'transparent',
                  backgroundColor: 'grey.900',
                }}
              >
                <Box sx={{ opacity: '70%', color: '#38bdf8' }}>{p.icon}</Box>
                <div>
                  <Typography fontWeight="medium" gutterBottom color="text.primary">
                    {p.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'grey.400' }}>
                    {p.body}
                  </Typography>
                </div>
              </Stack>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}

// ─── Section: How It Works ───────────────────────────────────────────────────
const HOW_STEPS = [
  {
    number: '01',
    icon: <BallotRoundedIcon sx={{ fontSize: 36 }} />,
    title: 'Configure your trial protocol',
    description: 'Select phase (I–IV), therapeutic area, number of arms, visit schedule, and primary endpoint. Takes under 2 minutes.',
  },
  {
    number: '02',
    icon: <TuneRoundedIcon sx={{ fontSize: 36 }} />,
    title: 'Generate SDTM-compliant subjects',
    description: 'Cynthia produces realistic subject records with MedDRA-coded AEs, lab values, disposition outcomes, and demographic distributions — all CDISC-aligned.',
  },
  {
    number: '03',
    icon: <DownloadRoundedIcon sx={{ fontSize: 36 }} />,
    title: 'Export SDTM ZIP or call the API',
    description: 'Download a complete trial dataset as a SDTM ZIP for your EDC, or hit the REST API to stream subjects directly into your data pipeline.',
  },
];

function HowItWorks() {
  return (
    <Container
      id="ls-how-it-works"
      sx={{
        pt: { xs: 4, sm: 12 },
        pb: { xs: 8, sm: 16 },
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: { xs: 3, sm: 6 },
      }}
    >
      <Box sx={{ width: { sm: '100%', md: '60%' }, textAlign: { sm: 'left', md: 'center' } }}>
        <Typography component="h2" variant="h4" color="text.primary">
          From zero to SDTM dataset in minutes
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
          No custom scripting. No data governance approvals. Just realistic, reproducible clinical data.
        </Typography>
      </Box>
      <Grid container spacing={4}>
        {HOW_STEPS.map((s) => (
          <Grid item xs={12} md={4} key={s.number}>
            <Stack spacing={2}>
              <Typography
                sx={{
                  fontSize: '3rem',
                  fontWeight: 900,
                  color: 'primary.main',
                  opacity: 0.2,
                  lineHeight: 1,
                  fontFamily: 'monospace',
                }}
              >
                {s.number}
              </Typography>
              <Box sx={{ color: 'primary.main', opacity: 0.8 }}>{s.icon}</Box>
              <Typography color="text.primary" fontWeight="bold">{s.title}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.75 }}>
                {s.description}
              </Typography>
            </Stack>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
}

// ─── Section: Features Grid ──────────────────────────────────────────────────
const FEATURES = [
  {
    icon: <AssignmentRoundedIcon />,
    title: 'Full SDTM Domain Coverage',
    description: 'DM, AE, LB, VS, EX, DS domains generated with correct variable names, controlled terminology, and realistic value ranges.',
  },
  {
    icon: <BiotechRoundedIcon />,
    title: '7 Therapeutic Areas',
    description: 'Oncology, Cardiovascular, CNS, Immunology, Infectious Disease, Rare Disease — each with area-specific AE profiles and lab panels.',
  },
  {
    icon: <ReportProblemRoundedIcon />,
    title: 'MedDRA-Coded Adverse Events',
    description: 'AEs are coded to MedDRA PT/SOC per therapeutic area, with realistic severity distributions and time-to-onset modeling.',
  },
  {
    icon: <ScienceRoundedIcon />,
    title: 'Multi-Arm Trial Designs',
    description: 'Configure parallel-arm, crossover, or dose-escalation designs. Subjects are assigned and tracked across arms with full exposure records.',
  },
  {
    icon: <ApiRoundedIcon />,
    title: 'REST API Access',
    description: 'Generate subjects programmatically via API. Ideal for CI/CD pipelines, EDC load testing, or seeding QA environments on every deploy.',
  },
  {
    icon: <VerifiedUserRoundedIcon />,
    title: 'HIPAA-Free by Design',
    description: 'Purely synthetic — zero IRB requirements, no BAAs, no de-identification audits. Use freely in dev, staging, and training environments.',
  },
];

function FeaturesGrid() {
  return (
    <Box
      id="ls-capabilities"
      sx={{
        pt: { xs: 4, sm: 12 },
        pb: { xs: 8, sm: 16 },
        color: 'white',
        bgcolor: '#06090a',
        borderTop: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Container
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: { xs: 3, sm: 6 },
        }}
      >
        <Box sx={{ width: { sm: '100%', md: '60%' }, textAlign: { sm: 'left', md: 'center' } }}>
          <Typography component="h2" variant="h4" color="text.primary">
            Capabilities
          </Typography>
          <Typography variant="body1" sx={{ color: 'grey.400', mt: 1 }}>
            Built for CROs, health-tech platforms, and clinical data engineers who need reliable, standards-compliant test data.
          </Typography>
        </Box>
        <Grid container spacing={2.5}>
          {FEATURES.map((f) => (
            <Grid item xs={12} sm={6} md={4} key={f.title}>
              <Stack
                direction="column"
                color="inherit"
                component={Card}
                spacing={1}
                useFlexGap
                sx={{
                  p: 3,
                  height: '100%',
                  border: '1px solid',
                  borderColor: 'grey.800',
                  background: 'transparent',
                  backgroundColor: 'grey.900',
                }}
              >
                <Box sx={{ opacity: '50%' }}>{f.icon}</Box>
                <div>
                  <Typography fontWeight="medium" gutterBottom color="text.primary">
                    {f.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'grey.400' }}>
                    {f.description}
                  </Typography>
                </div>
              </Stack>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}

// ─── Section: Stats ──────────────────────────────────────────────────────────
const STATS = [
  { value: '78%', label: 'of generated subjects complete the trial', color: '#34d399' },
  { value: '7', label: 'therapeutic areas out of the box', color: '#38bdf8' },
  { value: 'Day One', label: 'CDISC SDTM-compliant, no configuration required', color: '#818cf8' },
];

function StatsSection() {
  return (
    <Box
      sx={{
        pt: { xs: 4, sm: 12 },
        pb: { xs: 8, sm: 16 },
        borderTop: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Container
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: { xs: 3, sm: 6 },
        }}
      >
        <Box sx={{ width: { sm: '100%', md: '60%' }, textAlign: { sm: 'left', md: 'center' } }}>
          <Typography component="h2" variant="h4" color="text.primary">
            By the numbers
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
            Realistic synthetic data that reflects real clinical trial dynamics.
          </Typography>
        </Box>

        <Grid container spacing={4} justifyContent="center">
          {STATS.map((s) => (
            <Grid item xs={12} sm={4} key={s.value}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography
                  sx={{
                    fontSize: 'clamp(2.8rem, 6vw, 4rem)',
                    fontWeight: 900,
                    color: s.color,
                    lineHeight: 1,
                    mb: 1.5,
                    fontFamily: 'monospace',
                    textShadow: `0 0 40px ${s.color}40`,
                  }}
                >
                  {s.value}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 220, mx: 'auto', lineHeight: 1.6 }}>
                  {s.label}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ textAlign: 'center' }}>
          <Typography
            variant="overline"
            color="text.disabled"
            sx={{ display: 'block', mb: 2, letterSpacing: '0.08em' }}
          >
            Realistic disposition modeling
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="center" flexWrap="wrap">
            {[
              { label: 'Completed', pct: '78%', color: '#34d399' },
              { label: 'Adverse Event', pct: '8%', color: '#f87171' },
              { label: 'Withdrawal', pct: '7%', color: '#fb923c' },
              { label: 'Protocol Deviation', pct: '4%', color: '#facc15' },
              { label: 'Other', pct: '3%', color: '#64748b' },
            ].map((d) => (
              <Chip
                key={d.label}
                label={`${d.label} ${d.pct}`}
                size="small"
                sx={{
                  backgroundColor: 'grey.900',
                  border: '1px solid',
                  borderColor: 'grey.800',
                  color: d.color,
                  fontWeight: 600,
                  fontSize: '0.75rem',
                }}
              />
            ))}
          </Stack>
        </Box>
      </Container>
    </Box>
  );
}

// ─── Section: Final CTA ───────────────────────────────────────────────────────
function CtaSection() {
  return (
    <Box
      sx={{
        pt: { xs: 4, sm: 12 },
        pb: { xs: 8, sm: 16 },
        textAlign: 'center',
        bgcolor: '#06090a',
        borderTop: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Container maxWidth="sm">
        <Typography
          variant="h4"
          component="h2"
          color="text.primary"
          sx={{ fontWeight: 800, mb: 2, lineHeight: 1.2 }}
        >
          Ready to stop hand-crafting test data?
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.75 }}>
          Get CDISC SDTM-compliant synthetic trial data in minutes. Free to start,
          scales with your study portfolio.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
          <Button
            component={Link}
            to="/signup"
            variant="contained"
            size="large"
            sx={{ fontWeight: 700, px: 5, py: 1.5, fontSize: '1rem' }}
          >
            Start Free
          </Button>
          <Button
            variant="outlined"
            size="large"
            component="a"
            href="mailto:sales@bacclabs.io"
            sx={{ fontWeight: 600, px: 4, py: 1.5, fontSize: '1rem' }}
          >
            Talk to Sales
          </Button>
        </Stack>
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 3 }}>
          No credit card required · SDTM ZIP export · REST API included
        </Typography>
      </Container>
    </Box>
  );
}

// ─── Page Root ────────────────────────────────────────────────────────────────
export default function LifeSciencesPage() {
  const [mode, setMode] = React.useState('dark');
  const LPtheme = createTheme(getLPTheme(mode));
  const toggleColorMode = () => setMode((prev) => (prev === 'dark' ? 'light' : 'dark'));

  return (
    <ThemeProvider theme={LPtheme}>
      <CssBaseline />
      <AppAppBar mode={mode} toggleColorMode={toggleColorMode} />
      <Hero />
      <Box sx={{ bgcolor: 'background.default' }}>
        <PainPoints />
        <HowItWorks />
        <Divider />
        <FeaturesGrid />
        <Divider />
        <StatsSection />
        <Divider />
        <Pricing />
        <Divider />
        <FAQ
          questions={LS_QUESTIONS}
          title="Life sciences FAQ"
          id="ls-faq"
        />
        <Divider />
        <CtaSection />
        <Divider />
        <Footer />
      </Box>
    </ThemeProvider>
  );
}
